import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { FAQ } from '../../widgets/FAQ';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';
import ColumnSummary from '../../widgets/ColumnSummary';
import WhySitePen from '../../widgets/WhySitePen';

const backEndServicesImg = require('../../assets/images/icon-illustration/back-end-services.svg');
const databaseTechnologyImg = require('../../assets/images/icon-illustration/database-technology.svg');
const backEndToolsImg = require('../../assets/images/icon-illustration/back-end-tools.svg');
const cloudAppImg = require('../../assets/images/icon-illustration/cloud-application.svg');

const factory = create();

export const NodeDevelopment = factory(function NodeDevelopment() {
	return (
		<div>
			<Head
				title="Node JS Development Services | SitePen"
				description="SitePen is an elite onshore node.js development company providing node.js development services with expertise in a wide range of framework technologies."
			/>
			<Hero background="level3Red">
				{{
					xlTitle: (
						<div>
							Server-side
							<br />
							JavaScript
						</div>
					)
				}}
			</Hero>
			<Section>
				<ContentWrapper>
					<WhySitePen reverse>
						{{
							title: 'Why Choose SitePen as Your Node.js Development Company',
							blurb: (
								<p>
									SitePen has been a powerhouse of JavaScript development since
									the company’s inception. Our expertise lies in end-to-end
									software solution delivery that includes using Node.js for
									server-side components when implementing JavaScript solutions.
									Our team has experience delivering full-stack JavaScript and
									Node.js solutions for the earliest stage startups to the largest
									multinational enterprise corporations and can help in the
									successful delivery of your products.
								</p>
							)
						}}
					</WhySitePen>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<ColumnSummary columnCount={2}>
						{{
							title: 'Node.js JavaScript Development Services',
							blurb: (
								<virtual>
									SitePen provides a broad range of expertise across many
									languages and technologies, with Node.js being a key component
									in our preferred technology stack for large-scale web
									applications where server-side processing is required.
								</virtual>
							),
							items: [
								{
									title: 'Node.js Server-Side Development',
									blurb: (
										<virtual>
											SitePen’s expertise in API design, implementation, and
											integration allow us to deliver robust Node.js
											server-side components within the context of a more
											comprehensive multi-layered software solution. Our team
											has designed, implemented, and uplifted a wide variety
											of back-end systems and architectures, including
											service-oriented architectures, systems designed around
											message-oriented middleware, and the migration of
											monolith backends to single-responsibility
											microservices. SitePen can augment your existing
											technology organization to deliver large-scale web
											application backends. We can also operate as a
											standalone delivery function to implement end-to-end web
											applications if required.
										</virtual>
									),
									image: (
										<Image
											src={backEndServicesImg}
											lazy
											size="icon"
											constrainedBy="height"
											align="center"
										/>
									)
								},
								{
									title: 'Node.js API Development',
									blurb: (
										<virtual>
											At SitePen, we are experts in delivering API services,
											whether as a complete API-driven product or as a
											component within a more comprehensive full-stack
											solution. We can help design and implement robust,
											efficient, secure, and elegant APIs that are easily
											maintainable over the long term. Our expertise in
											delivering full-stack solutions, business-to-customer,
											and business-to-business API products means we fully
											appreciate the complexities and life cycle of APIs,
											which can significantly help you with your API product
											needs.
										</virtual>
									),
									image: (
										<Image
											src={databaseTechnologyImg}
											lazy
											size="icon"
											constrainedBy="height"
											align="center"
										/>
									)
								},
								{
									title: 'Node.js Web App Development',
									blurb: (
										<virtual>
											If you are looking for an end-to-end web application
											delivery partner, SitePen can help. Our team can design
											and implement server-side, data store, and user
											interface layers to deliver a complete JavaScript-based
											web application solution. However, our expertise is not
											limited to JavaScript; our philosophy is always to
											choose the right tool for the job and use the most
											effective full-stack web application technology
											ecosystem that works best for your organization.
											JavaScript and Node.js are great tools to have as part
											of that strategy.
										</virtual>
									),
									image: (
										<Image
											src={backEndToolsImg}
											lazy
											size="icon"
											constrainedBy="height"
											align="center"
										/>
									)
								},
								{
									title: 'Serverless Node.js Development',
									blurb: (
										<virtual>
											SitePen provides expertise for both traditional
											infrastructure stacks and cloud-based serverless Node.js
											application delivery across various public cloud
											vendors. Our architects can analyze and design the
											infrastructure and vendor service capability stack that
											best meets the needs of your web application and budget.
											With an infrastructure deployment strategy and
											scalability goals agreed upon, our engineers can deliver
											serverless Node.js applications that best meet your
											requirements.
										</virtual>
									),
									image: (
										<Image
											src={cloudAppImg}
											lazy
											size="icon"
											constrainedBy="height"
											align="center"
										/>
									)
								}
							]
						}}
					</ColumnSummary>
				</ContentWrapper>
			</Section>
			<Section>
				<ConnectStrip />
			</Section>
			<Section type="trailing" shaded>
				<FAQ title="FAQs">
					{[
						{
							title: 'Why Use Node.js For Web App Development?',
							content: (
								<p>
									Node.js has been the preferred industry choice for server-side
									JavaScript development for over a decade. It supports a vast
									ecosystem of libraries, frameworks, and middleware components
									together with a large pool of developer and vendor expertise.
									This extensive support allows for rapid, simplified development
									of large-scale applications through a robust pool of worldwide
									developer talent.
								</p>
							)
						},
						{
							title: 'Why Hire SitePen Node.js Developers?',
							content: (
								<p>
									SitePen’s expertise lies in the end-to-end delivery of
									full-stack software solutions, not limited to web applications.
									Our team can handle all aspects of the software delivery
									lifecycle, including strategy, requirements analysis,
									architecture design, implementation, devops automation,
									infrastructure design, and production launch. We understand the
									complexities and demands of a Node.js service layer within a
									complete web application solution, such as rigorous API design
									and lifecycle management, effective service composition,
									performance, scalability, and security. This breadth of
									experience across a wide range of industries and solution scales
									allows SitePen to deliver efficient and robust Node.js
									server-side components as part of a complete delivery project.
								</p>
							)
						},
						{
							title: 'Which Javascript Framework is Best?',
							content: (
								<p>
									The best framework is the one that allows for the simplest, most
									efficient, and most secure implementation to solve the task at
									hand. JavaScript is a jack of all trades language, and Node.js
									allows for implementing a wide variety of applications using the
									language. To find the “best framework”, it is essential first to
									understand what problem needs to be solved and then assess the
									set of available frameworks that can help implement a solution.
									Due to JavaScript’s popularity, frameworks come and go with
									great regularity, meaning additional effort is needed to judge
									which framework will be supportable both now and in the future.
									A useful framework is one that will have robust vendor or
									community support for as long as possible within your product’s
									total lifespan. With SitePen’s extensive history in delivering
									JavaScript solutions, we are in a great position to help you
									assess what framework and technologies will best meet your
									needs.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default NodeDevelopment;
